import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { BreadcrumbsItem, BreadcrumbsViewModel } from 'src/common/models/common/breadcrumbs-model';

import { AnalyticsConfig, classNames } from 'uitk-react-utils';
import { UitkBreadcrumbs } from 'uitk-react-breadcrumbs';

interface BreadcrumbsProps {
  breadcrumbsContent?: BreadcrumbsViewModel;
  isDateless?: boolean;
  analytics?: AnalyticsStore;
}

interface DisplayBreadcrumbsProps {
  crumbsContent: BreadcrumbsItem[];
  crumbsToDisplay?: number;
  handleClick?: () => void;
  menuAnalytics?: AnalyticsConfig;
  showMenu?: boolean;
}

interface DisplayBreadcrumbsState {
  showMenu: boolean;
  crumbsToDisplay: {
    viewSmall: number | null;
    viewMedium: number | null;
    viewLarge: number | null;
  };
}

@inject('analytics')
export class Breadcrumbs extends React.Component<BreadcrumbsProps, DisplayBreadcrumbsState> {
  constructor(props) {
    super(props);

    this.state = {
      showMenu: false,
      crumbsToDisplay: {
        viewSmall: null,
        viewMedium: null,
        viewLarge: null
      }
    };
    this.handleClick = this.handleClick.bind(this);
  }

  public componentDidMount() {
    this.setState({
      crumbsToDisplay: {
        viewSmall: 1,
        viewMedium: 2,
        viewLarge: 1
      }
    });
  }

  public handleClick() {
    this.setState({
      showMenu: !this.state.showMenu
    });
  }

  public render() {
    const { breadcrumbsContent, isDateless, analytics } = this.props;
    const { showMenu, crumbsToDisplay } = this.state;

    if (!breadcrumbsContent || !breadcrumbsContent.crumbs || breadcrumbsContent.crumbs.length === 0 || !isDateless) {
      return null;
    }

    breadcrumbsContent.crumbs.forEach((crumb, index) => {
      breadcrumbsContent.crumbs[index].analytics = analytics.setupTracker('BreadcrumbItem.Click', 'User interacts with breadcrumb item');
    });

    const menuAnalytics: AnalyticsConfig = analytics.setupTracker('Breadcrumbs.Menu', 'User interacts with breadcrumbs menu');

    return (
      <Viewport>
        <ViewSmall>
          <DisplayBreadcrumbs
            crumbsContent={breadcrumbsContent.crumbs}
            crumbsToDisplay={crumbsToDisplay.viewSmall}
            handleClick={this.handleClick}
            menuAnalytics={menuAnalytics}
            showMenu={showMenu}
          />
        </ViewSmall>
        <ViewMedium>
          <DisplayBreadcrumbs
            crumbsContent={breadcrumbsContent.crumbs}
            crumbsToDisplay={crumbsToDisplay.viewMedium}
            handleClick={this.handleClick}
            menuAnalytics={menuAnalytics}
            showMenu={showMenu}
          />
        </ViewMedium>
        <ViewLarge>
          <DisplayBreadcrumbs
            crumbsContent={breadcrumbsContent.crumbs}
            crumbsToDisplay={crumbsToDisplay.viewLarge}
            handleClick={this.handleClick}
            menuAnalytics={menuAnalytics}
            showMenu={showMenu}
          />
        </ViewLarge>
      </Viewport>
    );
  }
}

export const DisplayBreadcrumbs = ({ crumbsContent, crumbsToDisplay, handleClick, menuAnalytics, showMenu }: DisplayBreadcrumbsProps) => {
  if (!crumbsContent || crumbsContent.length === 0) {
    return null;
  }

  const cssClasses = classNames('all-x-padding-three uitk-cell', {
    'all-t-padding-two': !crumbsToDisplay
  });

  return (
    <div className={cssClasses} itemScope={true} itemType="https://schema.org/WebPage">
      <div itemProp="breadcrumb" itemScope={true} itemType="https://schema.org/BreadcrumbList" typeof="v:Breadcrumb">
        {crumbsContent.map(crumb => {
          if (crumbsContent.length > crumb.index) {
            return (
              <div key={crumb.fullUrl} itemProp="itemListElement" itemScope={true} itemType="http://schema.org/ListItem">
                <meta itemProp="item" content={crumb.fullUrl} />
                <meta itemProp="name" content={crumb.text} />
                <meta itemProp="url" content={crumb.url} />
                <meta itemProp="position" content={crumb.index.toString()} />
              </div>
            );
          }
        })}
        <UitkBreadcrumbs
          crumbs={crumbsContent}
          crumbsToDisplay={crumbsToDisplay}
          isOpen={showMenu}
          onTriggerClick={handleClick}
          menuAnalytics={menuAnalytics}
        />
      </div>
    </div>
  );
};

DisplayBreadcrumbs.displayName = 'DisplayBreadcrumbs';
